import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { type ReactNode } from "react";

import {
  type SessionCompositionOutcome,
  type SessionRecord,
} from "@/shared/domain/sessions";
import { useDatasetSessions } from "@/shared/domain/sessions/hooks";
import { Button, Combobox } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import { SessionCompositionStatus } from "../SessionCompositionStatus";

import styles from "./AddToSessionDialog.module.css";
import { type AddToSessionController } from "./useAddToSessionController";

const STEP_LABELS = ["Choose session", "Results"];

/** A two-step dialog: choose the session, then read what happened to each file. Renders the state
 * of `useAddToSessionController` and holds none of its own. */
export function AddToSessionDialog({
  controller,
}: {
  controller: AddToSessionController;
}) {
  const { snapshot, outcome } = controller;
  if (!snapshot) {
    return null;
  }
  return (
    <Dialog open onClose={controller.close} fullWidth maxWidth="sm">
      <DialogTitle>Add to existing session</DialogTitle>
      <DialogContent className={styles.content}>
        <Stepper activeStep={activeStep(outcome)}>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className={styles.stepBody}>
          <StepBody controller={controller} datasetId={snapshot.datasetId} />
        </div>
      </DialogContent>
      <DialogActions>
        <CancelAction controller={controller} />
        <PrimaryAction controller={controller} />
      </DialogActions>
    </Dialog>
  );
}

function everyFileIncluded(outcome: SessionCompositionOutcome) {
  return outcome.entries.every((entry) => entry.status === "included");
}

/** The `activeStep` for the stepper: 0 while choosing the session, 1 while the results leave files
 * unresolved, and one past the last step once every file is in the session, which makes the
 * stepper mark both steps complete. */
function activeStep(outcome: SessionCompositionOutcome | undefined) {
  if (!outcome) {
    return 0;
  }
  return everyFileIncluded(outcome) ? STEP_LABELS.length : 1;
}

function StepBody({
  controller,
  datasetId,
}: {
  controller: AddToSessionController;
  datasetId: string;
}) {
  const { outcome } = controller;
  if (!outcome) {
    return (
      <>
        <SessionChoice controller={controller} datasetId={datasetId} />
        <ContextChangedNotice matches={controller.contextMatches} />
        <SelectionNotice matches={controller.selectionMatches} />
        <ErrorNotice message={controller.error} />
      </>
    );
  }
  return (
    <>
      <p className={styles.chosenSession}>
        Session: {sessionLabel(controller.selectedSession)}
      </p>
      <ResultsContextNotice matches={controller.contextMatches} />
      <ErrorNotice message={controller.error} />
      <Results controller={controller} outcome={outcome} />
    </>
  );
}

function SessionChoice({
  controller,
  datasetId,
}: {
  controller: AddToSessionController;
  datasetId: string;
}) {
  if (!controller.contextMatches) {
    return null;
  }
  return <SessionOptions controller={controller} datasetId={datasetId} />;
}

function sessionLabel(session: SessionRecord | null): string {
  if (!session) {
    return "";
  }
  return session.name
    ? `${session.name} (${session.session_id})`
    : session.session_id;
}

/** Mounted only while the user is choosing a session, so the search that fetches every page of the
 * dataset's sessions stops once the dialog shows results. */
function SessionOptions({
  controller,
  datasetId,
}: {
  controller: AddToSessionController;
  datasetId: string;
}) {
  const { sessions, isLoading, error } = useDatasetSessions(datasetId);
  let emptyMessage = "No matching sessions";
  if (isLoading) {
    emptyMessage = "Loading sessions";
  } else if (sessions.length === 0) {
    emptyMessage = "No sessions in this dataset";
  }
  return (
    <>
      <Combobox
        items={sessions}
        value={controller.selectedSession}
        onValueChange={controller.selectSession}
        label="Session"
        itemToStringLabel={sessionLabel}
        isItemEqualToValue={(a, b) => a.session_id === b.session_id}
        disabled={controller.phase !== "idle"}
      >
        <Combobox.TriggerInput placeholder="Select a session" />
        <Combobox.Content>
          <Combobox.Empty>{emptyMessage}</Combobox.Empty>
          <Combobox.List>
            {(session: SessionRecord) => (
              <Combobox.Item key={session.session_id} value={session}>
                {sessionLabel(session)}
              </Combobox.Item>
            )}
          </Combobox.List>
        </Combobox.Content>
      </Combobox>
      <ErrorNotice
        message={
          error
            ? userFacingErrorMessage(
                error,
                "Failed to load sessions. Please retry.",
              )
            : undefined
        }
      />
    </>
  );
}

function ContextChangedNotice({ matches }: { matches: boolean }) {
  if (matches) {
    return null;
  }
  return (
    <Alert severity="info">
      The organization or dataset changed. Cancel and start again to add files
      from the current one.
    </Alert>
  );
}

function ResultsContextNotice({ matches }: { matches: boolean }) {
  if (matches) {
    return null;
  }
  return (
    <Alert severity="info">
      These results belong to another organization or dataset. Return there to
      continue, or close this dialog to discard them.
    </Alert>
  );
}

function SelectionNotice({ matches }: { matches: boolean }) {
  if (matches) {
    return null;
  }
  return (
    <Alert severity="info">
      The file selection changed. Cancel and start again to use the current
      files.
    </Alert>
  );
}

function ErrorNotice({ message }: { message?: ReactNode }) {
  return message ? <Alert severity="error">{message}</Alert> : null;
}

function Results({
  controller,
  outcome,
}: {
  controller: AddToSessionController;
  outcome: SessionCompositionOutcome;
}) {
  if (everyFileIncluded(outcome)) {
    const count = outcome.entries.length;
    return (
      <Alert severity="success">
        {count === 1
          ? "1 file was added to the session."
          : `${count} files were added to the session.`}
      </Alert>
    );
  }
  return (
    <>
      <SessionCompositionStatus outcome={outcome} />
      <RecoveryActions controller={controller} outcome={outcome} />
    </>
  );
}

function RecoveryActions({
  controller,
  outcome,
}: {
  controller: AddToSessionController;
  outcome: SessionCompositionOutcome;
}) {
  const hasRemainingFiles = outcome.entries.some(
    (entry) => entry.status === "refused" || entry.status === "notAttempted",
  );
  const unknownEntries = outcome.entries.filter(
    (entry) => entry.status === "unknown",
  );
  // Until a check has run there is no observation to act on, so an unchecked file is offered only
  // "Check unconfirmed files"; a checked one splits by what the check saw.
  const retriableUnknown = unknownEntries.filter(
    (entry) =>
      entry.reconciliationAttempted && entry.observation !== "conflict",
  );
  const conflictingUnknown = unknownEntries.filter(
    (entry) =>
      entry.reconciliationAttempted && entry.observation === "conflict",
  );
  const disabled = !controller.contextMatches || controller.phase !== "idle";
  return (
    <div className={styles.recoveryActions}>
      <RecoveryAction
        visible={hasRemainingFiles}
        disabled={disabled}
        onClick={() => {
          void controller.retryRemaining();
        }}
      >
        Retry remaining files
      </RecoveryAction>
      <RecoveryAction
        visible={unknownEntries.length > 0}
        disabled={disabled}
        onClick={() => {
          void controller.reconcile();
        }}
      >
        Check unconfirmed files
      </RecoveryAction>
      <RecoveryAction
        visible={retriableUnknown.length > 0}
        disabled={disabled}
        onClick={() => {
          void controller.retryUnknown(
            retriableUnknown.map((entry) => entry.originalIndex),
          );
        }}
      >
        Retry unconfirmed files
      </RecoveryAction>
      <RecoveryAction
        visible={conflictingUnknown.length > 0}
        disabled={disabled}
        onClick={() => {
          void controller.replaceConflicts(
            conflictingUnknown.map((entry) => entry.originalIndex),
          );
        }}
      >
        Replace start and end times
      </RecoveryAction>
    </div>
  );
}

function RecoveryAction({
  visible,
  ...props
}: {
  visible: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  if (!visible) {
    return null;
  }
  return <Button variant="outlined" {...props} />;
}

/** Offered until results are on screen, and again while a retry still has files to send. */
function CancelAction({ controller }: { controller: AddToSessionController }) {
  const { outcome, phase } = controller;
  if (outcome && (phase !== "submitting" || everyFileIncluded(outcome))) {
    return null;
  }
  return (
    <Button
      variant="text"
      disabled={controller.stopping}
      onClick={controller.close}
    >
      Cancel
    </Button>
  );
}

function PrimaryAction({ controller }: { controller: AddToSessionController }) {
  const { outcome, phase } = controller;
  if (!outcome) {
    return (
      <Button
        variant="contained"
        className={styles.primaryAction}
        loading={phase !== "idle"}
        disabled={
          !controller.contextMatches ||
          !controller.selectionMatches ||
          controller.selectedSession === null
        }
        onClick={() => {
          void controller.submit();
        }}
      >
        Add files
      </Button>
    );
  }
  return (
    <Button
      variant="contained"
      className={styles.primaryAction}
      disabled={phase === "submitting"}
      onClick={controller.close}
    >
      {everyFileIncluded(outcome) ? "Done" : "Close"}
    </Button>
  );
}
